import {
  useCreateOrganizationInvitation,
  useUserActiveOrganization
} from "@/api";
import { TypographyMuted } from "@/components/typography/Muted";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { Role } from "@/gql/__generated__/graphql";
import { cn } from "@/lib/utils";
import { DialogClose } from "@radix-ui/react-dialog";
import { useSearch } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useRef, useState } from "react";
import { validateEmail } from "./util/validateEmail";

type UserInvitation = {
  id: number;
  email: string;
  role?: Role;
  errorEmail?: boolean;
  errorRole?: boolean;
};

type Props = {
  setTabViewToPendingInvitations?: () => void;
};

const OrganizationInviteUser = ({ setTabViewToPendingInvitations }: Props) => {
  const { invite, email: paramEmail } = useSearch({
    from: "/_auth/_fixed-sidebar/_organization-access/organization/$organizationSlug/organization-management"
  });

  const { activeOrganization, loading: loadingActiveOrganization } =
    useUserActiveOrganization();
  const [inviteUserDialogOpen, setInviteUserDialogOpen] = useState(invite);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [invitations, setInvitations] = useState<UserInvitation[]>([
    { id: 0, email: paramEmail || "" }
  ]);

  const [createOrganizationInvitation] = useCreateOrganizationInvitation();
  const organizationName = activeOrganization?.name;
  const roles = Role;

  const [invalidValues, setInvalidValues] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  async function handleInvite({
    email,
    role
  }: UserInvitation): Promise<
    Pick<UserInvitation, "errorEmail" | "errorRole"> | undefined
  > {
    if (!email) return undefined;
    if (!role) return { errorRole: true };

    const isValidEmail = validateEmail(email);
    if (!isValidEmail) {
      console.error("Invalid email", email);
      return { errorEmail: true };
    }

    const submitResult:
      | Pick<UserInvitation, "errorEmail" | "errorRole">
      | undefined = await createOrganizationInvitation({
      variables: {
        organizationId: activeOrganization?.id || "",
        email,
        role
      }
    })
      .then((res) => {
        switch (res.data?.organizationInvitationCreate.__typename) {
          case "InvalidEmail":
            console.error(
              "Invalid email",
              res.data.organizationInvitationCreate.email
            );
            return { errorEmail: true };
          case "OrganizationInvitationExists":
          case "OrganizationInvitation":
            return undefined;
          default:
            return { errorEmail: true };
        }
      })
      .catch((error) => {
        console.error(error);
        return { errorEmail: true };
      });

    return submitResult;
  }

  async function handleInvites() {
    if (!activeOrganization?.id) return;
    setLoadingSubmit(true);

    const result = (
      await Promise.all(
        invitations.map(async (invitation) => {
          const res = await handleInvite(invitation);

          if (!res) return undefined;

          return { ...invitation, errorEmail: false, errorRole: false, ...res };
        })
      )
    ).filter(Boolean);

    setInvitations(
      result.length > 0 ? (result as UserInvitation[]) : [{ id: 0, email: "" }]
    );

    setTabViewToPendingInvitations?.();
    setLoadingSubmit(false);

    if (result.length < 1) {
      const sentInvitations = invitations.filter(
        (i) => i.email.length > 0
      ).length;
      if (sentInvitations > 0)
        toast({
          title: `Invitation${sentInvitations > 1 ? "s" : ""} sent`,
          description: `${sentInvitations} invitation${sentInvitations > 1 ? "s" : ""} to ${organizationName} has been sent.`
        });
      setInviteUserDialogOpen(false);
      return;
    }

    toast({
      title: "An error occurred",
      description:
        "One or more invitations failed to send. Try again or report the issue if the problem persists."
    });
  }

  return (
    <Dialog.Root
      open={inviteUserDialogOpen}
      onOpenChange={(open) => {
        setInviteUserDialogOpen(open);
        if (!open) setInvitations([{ id: 0, email: "" }]);
      }}
    >
      <Dialog.Trigger asChild>
        <Button
          variant="outline"
          size="default"
          className="flex flex-row items-center gap-1"
          data-testid="inviteUserTriggerButton"
        >
          <Plus size={12} />
          Invite users
        </Button>
      </Dialog.Trigger>

      <Dialog.Content
        data-testid="invitationDialogContent"
        className="sm:max-w-xl dark:bg-brand-gray-900"
      >
        <Dialog.Header className="space-y-2 relative">
          <Dialog.Title className="leading-5 text-brand-gray-800 dark:text-brand-gray-100 ">
            Invite users to {organizationName}
          </Dialog.Title>
          <Dialog.Description className="text-brand-gray-700 dark:text-brand-gray-300">
            Add the users&apos; emails and select their role within the
            organization.
          </Dialog.Description>
        </Dialog.Header>
        <form
          ref={formRef}
          className="grid grid-cols-[2fr_1fr] gap-2 mt-4 max-h-80 overflow-y-auto overflow-x-visible p-0.5 pt-0"
        >
          <Label className="text-brand-gray-700 dark:text-brand-gray-100 sticky top-0 pb-1.5 z-20 dark:bg-brand-gray-900">
            Email
          </Label>
          <Label className="text-brand-gray-700 dark:text-brand-gray-100 sticky top-0 pb-1.5 z-20 dark:bg-brand-gray-900">
            Role
          </Label>
          {invitations.map((invitation) => (
            <div
              className="grid grid-cols-[2fr_1fr] gap-2 col-span-2"
              key={invitation.id}
            >
              <Input
                type="email"
                autoFocus
                required
                placeholder="Email"
                data-testid="emailInput"
                className={cn(
                  invitation.errorEmail
                    ? "border-brand-error focus:ring-brand-error dark:focus:ring-brand-error"
                    : "dark:border-brand-gray-700"
                )}
                value={invitation.email}
                onChange={(e) =>
                  setInvitations((prev) =>
                    prev.map((prevInvitation) =>
                      prevInvitation.id === invitation.id
                        ? {
                            ...prevInvitation,
                            email: e.currentTarget.value
                          }
                        : prevInvitation
                    )
                  )
                }
              />
              <Select
                data-testid="roleInput"
                onValueChange={(e) =>
                  setInvitations((prev) =>
                    prev.map((prevInvitation) =>
                      prevInvitation.id === invitation.id
                        ? {
                            ...prevInvitation,
                            role: e.toUpperCase() as Role
                          }
                        : prevInvitation
                    )
                  )
                }
              >
                <SelectTrigger
                  data-testid="roleSelectTrigger"
                  className={cn(
                    invitation.errorRole
                      ? "border-brand-error focus:ring-brand-error dark:focus:ring-brand-error"
                      : "dark:border-brand-gray-700"
                  )}
                >
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent className="dark:bg-brand-gray-900 dark:border-brand-gray-700">
                  <SelectGroup>
                    <SelectLabel>Roles</SelectLabel>
                    {Object.keys(roles).map((role) => (
                      <SelectItem
                        data-testid="roleOption"
                        key={role}
                        value={role}
                        className="capitalize"
                      >
                        {role}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          ))}

          {invalidValues && (
            <TypographyMuted
              className={cn("mb-2 h-4 text-brand-error text-right italic")}
            >
              {invalidValues}
            </TypographyMuted>
          )}
        </form>

        <Button
          onClick={() =>
            setInvitations((prev) => [
              ...prev,
              { id: (prev.at(-1)?.id || 0) + 1, email: "" }
            ])
          }
          variant="ghost"
          className="justify-self-end"
        >
          Add more <Plus size={14} className="ml-1" />
        </Button>

        <Dialog.Footer className="mt-6">
          {loadingSubmit && <LoadingCircle className="my-auto mr-2" />}
          <DialogClose asChild>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setInvalidValues("")}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="default"
            type="submit"
            onClick={handleInvites}
            disabled={
              loadingActiveOrganization ||
              loadingSubmit ||
              (invitations.length < 2 &&
                invitations.some((i) => i.email.length < 1 || !i.role))
            }
          >
            Send invite{invitations.length > 1 ? "s" : ""}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default OrganizationInviteUser;
